import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Card, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import SatteliteMap from "./SatteliteMap";
import moment from "moment";
import api from "../api/api";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import LandscapeOutlinedIcon from "@mui/icons-material/LandscapeOutlined";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./SatelliteImages.css";
import ndvi from "../assets/images/NDVI.png";
import ndwi from "../assets/images/NDWI.png";
import moisture from "../assets/images/MOISTURE-INDEX.png";
import swir from "../assets/images/SWIR.png";
import terrain from "../assets/images/terrain.png";
import Swal from "sweetalert2";

const SATELLITE_HISTORY_WEEKS = 520;

const parseFieldBoundary = (value) => {
  try {
    if (typeof value !== "string" || value.trim() === "") return null;

    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed) || parsed.length < 3) return null;

    const coordinates = parsed.map((point) => {
      const longitude = Number(point?.Long ?? point?.lng ?? point?.lon);
      const latitude = Number(point?.Lat ?? point?.lat);

      if (
        !Number.isFinite(latitude) ||
        !Number.isFinite(longitude) ||
        latitude < -90 ||
        latitude > 90 ||
        longitude < -180 ||
        longitude > 180
      ) {
        throw new Error("Invalid field boundary point");
      }

      return [longitude, latitude];
    });

    const distinctPoints = new Set(
      coordinates.map(([longitude, latitude]) => `${longitude},${latitude}`)
    );
    if (distinctPoints.size < 3) return null;

    const first = coordinates[0];
    const last = coordinates[coordinates.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
      coordinates.push([...first]);
    }

    return coordinates;
  } catch (_error) {
    return null;
  }
};

const formatCoordinate = (value) => {
  const coordinate = Number(value);
  return Number.isFinite(coordinate) ? coordinate.toFixed(5) : "—";
};

const SatelliteImages = () => {
  const { t } = useTranslation();
  const [fields, setFields] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [selectedField, setSelectedField] = useState([]);
  const [satellitesImages, setSatellitesImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);

  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  // Keep dates in an ISO format internally. Localized strings such as
  // "2 août 2026" must only be used for display, never for API requests.
  const [selectedDate, setSelectedDate] = useState(() =>
    moment().format("YYYY-MM-DD")
  );
  const [weekAnchor, setWeekAnchor] = useState(() => moment().startOf("isoWeek"));
  const [calendarOpen, setCalendarOpen] = useState(false);
  const calendarPopoverRef = useRef(null);

  const [selectedImageType, setSelectedImageType] = useState(null);
  const [pixelValue, setPixelValue] = useState(null);
  const pixelRequestRef = useRef(0);
  const pixelCacheRef = useRef(new Map());

  const resetPixelLookup = () => {
    pixelRequestRef.current += 1;
    pixelCacheRef.current.clear();
    setPixelValue(null);
  };

  useEffect(() => {
    if (!calendarOpen) return undefined;

    const closeCalendar = (event) => {
      if (!calendarPopoverRef.current?.contains(event.target)) {
        setCalendarOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setCalendarOpen(false);
    };

    document.addEventListener("pointerdown", closeCalendar);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeCalendar);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [calendarOpen]);
  function tryExtractMessage(errorString) {
    try {
      if (!errorString) return "Unknown error.";

      const clean = errorString.replace("Sentinel API error: ", "");

      // Try JSON parsing first
      const jsonMatch = clean.match(/\{.*\}/s);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        return (
          parsed.error?.message ||
          parsed.message ||
          "An unknown error occurred."
        );
      }

      // Check for common raw HTML/server message
      if (clean.includes("HTTP ERROR 500")) {
        return "The Sentinel service encountered an internal error while processing the image. Try again later or adjust the parameters.";
      }

      // Default cleanup
      const lines = clean
        .split("\n")
        .map((l) => l.trim())
        .filter(
          (l) =>
            l &&
            !l.startsWith("URI:") &&
            !l.startsWith("STATUS:") &&
            !l.startsWith("SERVLET:")
        );

      return lines.join(" ");
    } catch (err) {
      return "An unknown error occurred.";
    }
  }
  const getSatelliteImages = async () => {
    const selected = moment(selectedDate, "YYYY-MM-DD", true);

    if (!selected.isValid() || selected.isAfter(moment(), "day")) {
      Swal.fire({
        icon: "warning",
        title: t("invalid_date"),
        text: t("future_date_not_allowed"),
      });
      return;
    }

    const field = selectedField[0];
    const coordinates = parseFieldBoundary(field?.coordinates);
    if (!field || !coordinates) {
      setSatellitesImages([]);
      setSelectedImages([]);
      Swal.fire({
        icon: "warning",
        title: t("invalid_coordinates"),
        text: t("field_missing_geographic_boundary"),
        confirmButtonText: t("ok"),
      });
      return;
    }

    try {
      setLoadingImages(true);

      const fieldId = field.Id;
      let userUid = JSON.parse(localStorage.getItem("user") || "{}").id;

      const apiUrl = `/add-satellite-images/${userUid}/${fieldId}`;
      const response = await api.post(
        apiUrl,
        {
          date: selected.format("YYYY-MM-DD"),
          coordinates,
        }
      );

      const images = Array.isArray(response.data.images) ? response.data.images : [];
      setSatellitesImages(images);

      const { type, message } = response.data;

      const failed = images.filter((img) => img.error);
      const blanks = images.filter((img) => img.isBlank);
      const lowQuality = images.filter((img) => img.qualityFlag === "low");
      if (failed.length || blanks.length || lowQuality.length) {
        let html = "";

        if (failed.length) {
          html += `<b><i class="fas fa-circle-xmark" aria-hidden="true"></i> ${t("satellite_failed_images", { defaultValue: "Failed Images" })}:</b><ul>${failed
            .map((f) => `<li>${f.type.toUpperCase()}: ${f.error}</li>`)
            .join("")}</ul>`;
        }

        if (blanks.length) {
          const blankReason = t("satellite_blank_reason", {
            defaultValue: "No visual data (e.g., full cloud cover)",
          });
          html += `<b><i class="fas fa-triangle-exclamation" aria-hidden="true"></i> ${t("satellite_blank_images", { defaultValue: "Blank Images" })}:</b><ul>${blanks
            .map((f) => `<li>${f.type.toUpperCase()}: ${blankReason}</li>`)
            .join("")}</ul>`;
        }

        if (lowQuality.length) {
          const lowQualityReason = t("satellite_low_quality_reason", {
            defaultValue: "May be unclear due to clouds or weak signal",
          });
          html += `<b><i class="fas fa-triangle-exclamation" aria-hidden="true"></i> ${t("satellite_low_quality_images", { defaultValue: "Low-Quality Images" })}:</b><ul>${lowQuality
            .map((f) => `<li>${f.type.toUpperCase()}: ${lowQualityReason}</li>`)
            .join("")}</ul>`;
        }

        Swal.fire({
          icon: "warning",
          title: t("satellite_images_not_ideal"),
          html: html,
          confirmButtonText: t("understood"),
        });
      }
      if (response.data.type !== "success") {
        console.warn(
          "Image fetching not fully successful:",
          response.data.message
        );
      }
    } catch (error) {
      console.error("API error:", error);

      if (error.response?.data?.message === "invalid_satellite_date") {
        Swal.fire({
          icon: "warning",
          title: t("invalid_date"),
          text: t("satellite_fetch_error"),
          confirmButtonText: t("ok"),
        });
      } else if (error.response?.data?.message === "invalid_field_coordinates") {
        Swal.fire({
          icon: "warning",
          title: t("invalid_coordinates"),
          text: t("field_missing_geographic_boundary"),
          confirmButtonText: t("ok"),
        });
      } else if (error.response?.data?.images?.length) {
        const failed = error.response.data.images;

        const html = failed
          .map((f) => {
            const msg = tryExtractMessage(f.error);
            return `<div><b>${f.type.toUpperCase()}</b>: ${msg}</div>`;
          })
          .join("<hr>");

        Swal.fire({
          icon: "error",
          title: t("failed_to_fetch_satellite_images"),
          html: html,
          confirmButtonText: t("ok"),
        });
      } else {
Swal.fire({
  icon: "error",
  title: t("unexpected_error"),
  text: t("satellite_fetch_error"),
  confirmButtonText: t("ok"),
});
      }

    } finally {
      setLoadingImages(false);
    }
  };

  useEffect(() => {
    const getDataFields = async () => {
      await api.get("/field/fields").then((res) => {
        const newData = res.data.farms || [];
        let Fields = [];
        newData.forEach((item) => {
          let fields = item.fields;
          if (fields) {
            fields.filter((itemfield) => !itemfield.deleted_at).forEach((itemfield) => {
              Fields.push({
                title: itemfield.name,
                status: itemfield.status,
                description: itemfield.description,
                Uid: itemfield.uid,
                farm_id: itemfield.farm_id,
                Latitude: itemfield.Latitude,
                Longitude: itemfield.Longitude,
                coordinates: itemfield.coordinates,
                Id: itemfield.id,
              });
            });
          }
        });
        setFields(Fields);
      }).catch((error) => {
        console.error("Error loading fields:", error);
        setFields([]);
      }).finally(() => {
        setFieldsLoading(false);
      });
    };
    getDataFields();
  }, []);

  useEffect(() => {
    if (selectedField.length > 0) {
      getSatelliteImages();
    }
  }, [selectedField, selectedDate]);

  const handleDateClick = async (date) => {
    if (loadingImages) return;
    const normalizedDate = moment(date, "YYYY-MM-DD", true);
    if (!normalizedDate.isValid()) return;
    resetPixelLookup();
    setSelectedDate(normalizedDate.format("YYYY-MM-DD"));
  };

  const currentWeekStart = moment().startOf("isoWeek");
  const oldestTimelineWeek = moment(currentWeekStart).subtract(
    SATELLITE_HISTORY_WEEKS,
    "weeks"
  );
  const weekRanges = Array.from({ length: 4 }, (_, index) => {
    const start = moment(weekAnchor)
      .subtract(3 - index, "weeks")
      .startOf("isoWeek");
    return { start, end: moment(start).endOf("isoWeek") };
  });
  const selectedWeekStart = moment(selectedDate, "YYYY-MM-DD", true).startOf("isoWeek");
  const weekAnchorOffset = Math.max(
    0,
    currentWeekStart.diff(moment(weekAnchor).startOf("isoWeek"), "weeks")
  );
  const timelineValue =
    SATELLITE_HISTORY_WEEKS -
    Math.min(SATELLITE_HISTORY_WEEKS, weekAnchorOffset);
  const canGoToPreviousWeek = moment(weekAnchor).isAfter(
    oldestTimelineWeek,
    "week"
  );
  const canGoToNextWeek = moment(weekAnchor)
    .add(1, "week")
    .isSameOrBefore(currentWeekStart);

  const formatWeekRange = ({ start, end }) => {
    const locale = localStorage.getItem("local") || "fr";
    return `${t("satellite_from", { defaultValue: "Du" })} ${start
      .clone()
      .locale(locale)
      .format("D MMM")} ${t("satellite_to", { defaultValue: "au" })} ${end
      .clone()
      .locale(locale)
      .format("D MMM YYYY")}`;
  };

  const selectWeek = (week) => {
    if (loadingImages) return;
    setWeekAnchor(moment(week.start));
    handleDateClick(moment.min(moment(week.end), moment()).format("YYYY-MM-DD"));
  };

  const handleCalendarDateChange = (value) => {
    const date = Array.isArray(value) ? value[0] : value;
    const selectedMoment = moment(date);
    if (!selectedMoment.isValid() || selectedMoment.isAfter(moment(), "day")) return;

    setWeekAnchor(moment(selectedMoment).startOf("isoWeek"));
    handleDateClick(selectedMoment.format("YYYY-MM-DD"));
    setCalendarOpen(false);
  };

  const moveTimeline = (direction) => {
    setWeekAnchor((current) => {
      const next = moment(current).add(direction, "week").startOf("isoWeek");
      if (next.isAfter(currentWeekStart)) return moment(currentWeekStart);
      if (next.isBefore(oldestTimelineWeek)) return moment(oldestTimelineWeek);
      return next;
    });
  };

  const handleTimelineChange = (event) => {
    const weeksAgo = SATELLITE_HISTORY_WEEKS - Number(event.target.value);
    setWeekAnchor(
      moment(currentWeekStart).subtract(weeksAgo, "weeks").startOf("isoWeek")
    );
  };

  const renderDateNavigator = () => {
    if (selectedField.length === 0) return null;
    return (
      <section className="satellite-date-navigator" aria-label={t("satellite_history", { defaultValue: "Satellite history" })}>
        <div className="satellite-calendar-popover-anchor" ref={calendarPopoverRef}>
          <button
            type="button"
            className={`satellite-date-navigator-icon ${calendarOpen ? "is-active" : ""}`}
            onClick={() => setCalendarOpen((open) => !open)}
            aria-label={t("satellite_choose_date", { defaultValue: "Choose a date" })}
            aria-expanded={calendarOpen}
            disabled={loadingImages}
          >
            <CalendarMonthIcon aria-hidden="true" />
          </button>
          {calendarOpen && (
            <div className="satellite-calendar-popover">
              <Calendar
                onChange={handleCalendarDateChange}
                value={
                  selectedDate
                    ? moment(selectedDate, "YYYY-MM-DD", true).toDate()
                    : null
                }
                maxDate={new Date()}
                locale={`${localStorage.getItem("local") || "fr"}-${(
                  localStorage.getItem("local") || "fr"
                ).toUpperCase()}`}
                tileDisabled={() => loadingImages}
              />
            </div>
          )}
        </div>
        <div className="satellite-timeline-content">
          <div className="satellite-week-navigation-row">
            <button type="button" className="satellite-week-arrow" onClick={() => moveTimeline(-1)} disabled={loadingImages || !canGoToPreviousWeek} aria-label={t("previous_week", { defaultValue: "Previous week" })}>
              <ChevronLeftIcon />
            </button>
            <div className="satellite-week-list">
              {weekRanges.map((week) => {
                const isSelected = week.start.isSame(selectedWeekStart, "day");
                return (
                  <button type="button" key={week.start.format("YYYY-MM-DD")} className={`satellite-week-button ${isSelected ? "is-active" : ""}`} onClick={() => selectWeek(week)} disabled={loadingImages}>
                    {formatWeekRange(week)}
                  </button>
                );
              })}
            </div>
            <button type="button" className="satellite-week-arrow" onClick={() => moveTimeline(1)} disabled={loadingImages || !canGoToNextWeek} aria-label={t("next_week", { defaultValue: "Next week" })}>
              <ChevronRightIcon />
            </button>
          </div>
          <div className="satellite-timeline-slider-row">
            <span>{oldestTimelineWeek.format("YYYY")}</span>
            <input
              type="range"
              min="0"
              max={SATELLITE_HISTORY_WEEKS}
              value={timelineValue}
              onChange={handleTimelineChange}
              disabled={loadingImages}
              aria-label={t("satellite_history_cursor", { defaultValue: "History cursor" })}
            />
            <span>{currentWeekStart.format("YYYY")}</span>
          </div>
        </div>
      </section>
    );
  };
  const getSelectedField = (e) => {
    const selectedId = e.target.value;
    const selected = fields.filter((field) => String(field.Id) === selectedId);
    setSelectedField(selected);
    setSelectedImageUrl(null);
    setSelectedImageType(null);
    setSelectedImages([]);
    resetPixelLookup();
  };

  useEffect(() => {
    setSelectedImages(satellitesImages);
  }, [selectedDate, selectedField, satellitesImages]);

  const handleClick = (image) => {
    if (image) {
      const imageUrl = `${process.env.REACT_APP_BASE_URL}${image.image_url}`; // Prepend the backend URL
      setSelectedImageUrl(imageUrl); // Save the full image URL with localhost
      setSelectedImageType(image.type);
      resetPixelLookup();
    }
  };
  const designationImageMap = {
    ndvi: t("satellite_index_ndvi", { defaultValue: "Vegetation health" }),
    ndwi: t("satellite_index_ndwi", { defaultValue: "Water index" }),
    moisture: t("satellite_index_moisture", { defaultValue: "Vegetation moisture" }),
    swir: t("satellite_index_swir", { defaultValue: "Water stress" }),
  };
  const typeIcons = {
    ndvi,
    ndwi,
    moisture,
    swir,
  };
  const activeIndexLabel = selectedImageType
    ? designationImageMap[selectedImageType] || selectedImageType
    : t("satellite_terrain", { defaultValue: "Terrain" });

  const handleIndicatorHover = async (coordinates) => {
    if (!coordinates || !selectedImageType) {
      pixelRequestRef.current += 1;
      setPixelValue(null);
      return;
    }
    const selectedImage = selectedImages.find(
      (image) => image.type === selectedImageType && image.id
    );
    if (!selectedImage) return;

    // Sentinel pixels are 10-20 m wide. Rounding keeps the same map pixel from
    // generating a new paid API request for every tiny cursor movement.
    const cacheKey = `${selectedImage.id}:${selectedImageType}:${coordinates.latitude.toFixed(4)}:${coordinates.longitude.toFixed(4)}`;
    const cachedValue = pixelCacheRef.current.get(cacheKey);
    if (cachedValue) {
      setPixelValue({ ...cachedValue, ...coordinates, status: "success" });
      return;
    }

    const requestId = ++pixelRequestRef.current;
    setPixelValue({
      type: selectedImageType,
      ...coordinates,
      status: "loading",
    });
    try {
      const response = await api.post("/satellite/pixel-value", {
        imageId: selectedImage.id,
        type: selectedImageType,
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
      });
      if (requestId === pixelRequestRef.current) {
        pixelCacheRef.current.set(cacheKey, response.data);
        setPixelValue({ ...response.data, ...coordinates, status: "success" });
      }
    } catch (error) {
      if (requestId !== pixelRequestRef.current) return;
      const noData = error.response?.data?.message === "no_satellite_value_at_location";
      setPixelValue({
        type: selectedImageType,
        ...coordinates,
        status: "error",
        message: noData
          ? t("satellite_no_value_here", { defaultValue: "Aucune donnée à cet endroit" })
          : t("satellite_value_unavailable", { defaultValue: "Valeur satellite indisponible" }),
      });
    }
  };

  const renderImageGallery = () => {
    if (loadingImages) {
      return (
        <div className="satellite-gallery-state" aria-live="polite">
          <CircularProgress size={24} />
          <span>{t("loading")}</span>
        </div>
      );
    }

    if (selectedImages.length === 0) {
      return (
        <div className="satellite-gallery-state">
          <SatelliteAltIcon aria-hidden="true" />
          <span>
            {selectedField.length > 0
              ? t("no_satellite_images")
              : t("message_image")}
          </span>
        </div>
      );
    }

    return (
      <section className="satellite-gallery" aria-label={t("satellite_images_list")}>
        <div className="satellite-gallery-heading">
          <div>
            <span className="satellite-eyebrow">
              {t("satellite_available_layers", { defaultValue: "Available layers" })}
            </span>
            <strong className="satellite-gallery-title">
              {t("satellite_choose_index", { defaultValue: "Choose an analysis index" })}
            </strong>
          </div>
          <strong>{selectedImages.length + 1}</strong>
        </div>
        <div className="satellite-index-grid">
        {selectedImages.map((image) => {
          const fullImageUrl = `${process.env.REACT_APP_BASE_URL}${image.image_url}`;
          const isActive = selectedImageUrl === fullImageUrl;

          return (
          <button
            type="button"
            key={image.type}
            onClick={() => handleClick(image)}
            className={`satellite-index-button ${isActive ? "is-active" : ""}`}
            aria-pressed={isActive}
          >
            <span className="satellite-index-icon">
              <img
                src={typeIcons[image.type]}
                alt={image.type}
              />
            </span>
            {image.isBlank && (
              <span className="satellite-quality-badge is-danger">
                {t("satellite_badge_blank", { defaultValue: "Blank" })}
              </span>
            )}
            {image.qualityFlag === "low" && !image.isBlank && (
              <span className="satellite-quality-badge is-warning">
                {t("satellite_badge_low", { defaultValue: "Low" })}
              </span>
            )}
            <span className="satellite-index-name">
              {designationImageMap[image.type] || image.type}
            </span>
            <span className="satellite-index-code">{image.type.toUpperCase()}</span>
          </button>
          );
        })}
        <button
          type="button"
          className={`satellite-index-button ${selectedImageUrl === "" || selectedImageUrl === null ? "is-active" : ""}`}
          onClick={() => {
            setSelectedImageUrl("");
            setSelectedImageType(null);
            resetPixelLookup();
          }}
          aria-pressed={selectedImageUrl === "" || selectedImageUrl === null}
        >
          <span className="satellite-index-icon">
            <img
              src={terrain}
              alt="Terrain"
            />
          </span>
          <span className="satellite-index-name">
            {t("satellite_terrain", { defaultValue: "Terrain" })}
          </span>
          <span className="satellite-index-code">
            {t("satellite_base_map", { defaultValue: "BASE MAP" })}
          </span>
        </button>
        </div>
      </section>
    );
  };

  const renderMapLayerControls = () => (
    <aside
      className="satellite-map-layer-panel"
      aria-label={t("satellite_choose_index", { defaultValue: "Choose an analysis index" })}
    >
      <span className="satellite-map-layer-title">
        {t("satellite_layers", { defaultValue: "Layers" })}
      </span>
      {selectedImages.map((image) => {
        const fullImageUrl = `${process.env.REACT_APP_BASE_URL}${image.image_url}`;
        const isActive = selectedImageUrl === fullImageUrl;
        return (
          <button
            type="button"
            key={image.type}
            className={`satellite-map-layer-button ${isActive ? "is-active" : ""}`}
            onClick={() => handleClick(image)}
            aria-pressed={isActive}
            title={designationImageMap[image.type] || image.type}
          >
            <img src={typeIcons[image.type]} alt="" />
            <span>{image.type.toUpperCase()}</span>
          </button>
        );
      })}
      <button
        type="button"
        className={`satellite-map-layer-button ${selectedImageUrl === "" || selectedImageUrl === null ? "is-active" : ""}`}
        onClick={() => {
          setSelectedImageUrl("");
          setSelectedImageType(null);
          resetPixelLookup();
        }}
        aria-pressed={selectedImageUrl === "" || selectedImageUrl === null}
        title={t("satellite_terrain", { defaultValue: "Terrain" })}
      >
        <img src={terrain} alt="" />
        <span>{t("satellite_terrain", { defaultValue: "Terrain" })}</span>
      </button>
    </aside>
  );

  return (
    <Container fluid className="main-content-container satellite-page">
      <header className="satellite-page-header">
        <div className="satellite-page-title">
          <span className="satellite-eyebrow">SMARTFARM</span>
          <h1>{t("Satellite Images")}</h1>
          <p>
            {t("satellite_overview_subtitle", {
              defaultValue:
                "Explore vegetation health, moisture and water stress for every field.",
            })}
          </p>
        </div>
        <div className="satellite-header-actions">
          <div className="satellite-overview-chips" aria-label={t("Satellite Images")}>
            <span>
              <LandscapeOutlinedIcon aria-hidden="true" />
              <strong>{fields.length}</strong>
              {t("fields")}
            </span>
            <span>
              <GridViewRoundedIcon aria-hidden="true" />
              <strong>{selectedImages.length}</strong>
              {t("satellite_layers", { defaultValue: "layers" })}
            </span>
          </div>
          <button
            type="button"
            className="satellite-refresh-button"
            onClick={getSatelliteImages}
            disabled={selectedField.length === 0 || loadingImages}
          >
            <RefreshIcon className={loadingImages ? "is-spinning" : ""} fontSize="small" />
            <span>{loadingImages ? t("loading") : t("refresh")}</span>
          </button>
        </div>
      </header>

      <Container fluid className="satellite-workspace">
        <Row className="satellite-layout">
          <Col lg="8" md="12" sm="12" className="satellite-map-column">
            <div className="satellite-panel-heading">
              <div>
                <span className="satellite-eyebrow">
                  {t("satellite_workspace_title", { defaultValue: "Field analysis" })}
                </span>
                <h2>
                  {selectedField[0]?.title || t("select_field")}
                </h2>
              </div>
              {selectedField.length > 0 && (
                <div className="satellite-analysis-meta">
                  <span className="satellite-index-chip">
                    <GridViewRoundedIcon fontSize="inherit" />
                    {activeIndexLabel}
                  </span>
                  <span className="satellite-date-chip">
                    <CalendarMonthIcon fontSize="inherit" />
                    {moment(selectedDate, "YYYY-MM-DD", true)
                      .locale(localStorage.getItem("local") || "fr")
                      .format("D MMM YYYY")}
                  </span>
                </div>
              )}
            </div>
            <Row>
              <Col lg="12" md="12" sm="12" className="satellite-map-shell">
                <SatteliteMap
                  data={selectedField}
                  selectedImageUrl={selectedImageUrl}
                  renderImageGallery={renderImageGallery}
                  renderDateNavigator={renderDateNavigator}
                  renderMapLayerControls={renderMapLayerControls}
                  onIndicatorHover={handleIndicatorHover}
                  pixelValue={pixelValue}
                  selectedImageType={selectedImageType}
                />
              </Col>
            </Row>
          </Col>

          <Col lg="4" md="12" sm="12" className="satellite-sidebar-column">
            <Card className="satellite-sidebar-card">
              {fieldsLoading ? (
                <LinearProgress />
              ) : (
                <>
                  <Card.Header>
                    <div className="satellite-control-heading">
                      <span><PlaceOutlinedIcon aria-hidden="true" /></span>
                      <div>
                        <small>{t("satellite_step_one", { defaultValue: "Step 1" })}</small>
                        <label htmlFor="satellite-field-select">
                          {t("select_field")}
                        </label>
                      </div>
                    </div>
                    <Form.Select
                      id="satellite-field-select"
                      value={selectedField[0]?.Id || ""}
                      onChange={getSelectedField}
                    >
                      <option value="">{t("select_field")}</option>
                      {fields.map((field) => {
                        return (
                          <option key={field.Id} value={field.Id}>
                            {field.title}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Card.Header>
                  <Card.Body>
                    {selectedField.length > 0 &&
                      selectedField.map((field) => {
                        return (
                          <React.Fragment key={field.Id}>
                            <Row className="satellite-field-details">
                              <Col lg="12" md="12" sm="12">
                                <div className="satellite-coordinate-card">
                                  <small>{t("lat")}</small>
                                  <strong>{formatCoordinate(field.Latitude)}</strong>
                                </div>
                              </Col>
                              <Col lg="12" md="12" sm="12">
                                <div className="satellite-coordinate-card">
                                  <small>{t("lon")}</small>
                                  <strong>{formatCoordinate(field.Longitude)}</strong>
                                </div>
                              </Col>
                            </Row>
                            <div className="satellite-selected-field">
                              <span><LandscapeOutlinedIcon aria-hidden="true" /></span>
                              <div>
                                <small>{t("name_field")}</small>
                                <strong>{field.title}</strong>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      })}
                    {selectedField.length === 0 && (
                      <div className="satellite-empty-selection">
                        <span className="satellite-empty-icon">
                          <SatelliteAltIcon aria-hidden="true" />
                        </span>
                        <strong>
                          {t("satellite_start_analysis", { defaultValue: "Start your analysis" })}
                        </strong>
                        <p>
                          {t("message_image")}
                        </p>
                      </div>
                    )}
                  </Card.Body>
                </>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
    </Container>
  );
};

export default SatelliteImages;
